import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTransition from "../components/PageTransition";
import axios from "axios";
import toast from "react-hot-toast";
import { BASE_ENDPOINT } from "../helpers/Config";
import { FaSearch, FaSyncAlt, FaChevronLeft, FaChevronRight, FaInfoCircle, FaExchangeAlt, FaPeopleArrows, FaArrowRight } from "react-icons/fa";
import { AnimatePresence, motion } from "framer-motion";

function safeText(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}

function nfmt(v, digits = 2) {
    const num = Number(v);
    if (!Number.isFinite(num)) return safeText(v);
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: digits }).format(num);
}

function formatDateTime(v) {
    if (!v) return "—";
    const dt = new Date(v);
    if (!Number.isFinite(dt.getTime())) return safeText(v);
    return dt.toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    });
}

function LoadingRow({ cols = 6 }) {
    return (
        <tr>
            {Array.from({ length: cols }).map((_, i) => (
                <td key={i}>
                    <div className="placeholder-glow">
                        <span className="placeholder col-12" />
                    </div>
                </td>
            ))}
        </tr>
    );
}

function PaginationBar({ loading, pageNo, totalPages, onPrev, onNext, onJump }) {
    const pages = useMemo(() => {
        const n = Number(totalPages || 1);
        return Array.from({ length: n }, (_, i) => i + 1);
    }, [totalPages]);

    return (
        <div className="d-flex justify-content-between align-items-center">
            <div>
                <span className="text-muted small">
                    Page {pageNo} of {totalPages}
                </span>
            </div>

            <div className="d-flex align-items-center gap-1">
                <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onPrev}
                    disabled={loading || pageNo <= 1}
                    type="button"
                >
                    <FaChevronLeft />
                </button>

                <select
                    className="form-select form-select-sm mx-1"
                    style={{ width: "auto" }}
                    value={pageNo}
                    onChange={(e) => onJump(Number(e.target.value))}
                    disabled={loading || totalPages <= 1}
                >
                    {pages.map((p) => (
                        <option key={p} value={p}>
                            {p}
                        </option>
                    ))}
                </select>

                <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onNext}
                    disabled={loading || pageNo >= totalPages}
                    type="button"
                >
                    <FaChevronRight />
                </button>
            </div>
        </div>
    );
}

function P2PDetailsModal({ open, item, onClose }) {
    if (!open || !item) return null;

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="modal-backdrop show d-block"
                        style={{
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            zIndex: 1040,
                        }}
                    />

                    <div
                        className="modal show d-block"
                        tabIndex="-1"
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            zIndex: 1050,
                            overflowY: "auto",
                        }}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: -10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="modal-dialog modal-dialog-centered modal-lg"
                            onClick={(e) => e.stopPropagation()}
                            style={{ maxWidth: "900px" }}
                        >
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "12px" }}>
                                <div className="modal-header border-bottom-0 p-3 p2p-modal-header">
                                    <div className="d-flex align-items-center w-100 text-white">
                                        <div className="flex-grow-1">
                                            <h5 className="modal-title fw-bold mb-0" style={{ fontSize: "1.1rem" }}>
                                                <FaPeopleArrows className="me-2" />
                                                P2P History Details
                                            </h5>
                                        </div>
                                        <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label="Close" />
                                    </div>
                                </div>

                                <div className="modal-body p-3">
                                    <div className="row g-3">
                                        <div className="col-12">
                                            <div className="card border-0 bg-light">
                                                <div className="card-body p-3">
                                                    <div className="row g-3">
                                                        <div className="col-12 col-md-4">
                                                            <div className="text-muted small">Transfer ID</div>
                                                            <div className="fw-semibold mt-1" style={{ wordBreak: "break-all" }}>
                                                                {safeText(item?.transfer_id)}
                                                            </div>
                                                        </div>
                                                        <div className="col-12 col-md-4">
                                                            <div className="text-muted small">Date</div>
                                                            <div className="fw-semibold mt-1">{formatDateTime(item?.create_date)}</div>
                                                        </div>
                                                        <div className="col-12 col-md-4">
                                                            <div className="text-muted small">Amount</div>
                                                            <div className="fw-bold mt-1">{nfmt(item?.amount, 2)}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <div className="card border-0 bg-light h-100">
                                                <div className="card-body p-3">
                                                    <h6 className="fw-bold p2p-modal-from-label mb-3" style={{ fontSize: "0.95rem" }}>From User</h6>
                                                    <div className="mb-2">
                                                        <div className="text-muted small">User ID</div>
                                                        <div className="fw-semibold mt-1">{safeText(item?.from?.user_id)}</div>
                                                    </div>
                                                    <div className="mb-2">
                                                        <div className="text-muted small">Username</div>
                                                        <div className="fw-semibold mt-1">{safeText(item?.from?.username)}</div>
                                                    </div>
                                                    <div className="mb-2">
                                                        <div className="text-muted small">Name</div>
                                                        <div className="fw-semibold mt-1">{safeText(item?.from?.name)}</div>
                                                    </div>
                                                    <div className="mb-2">
                                                        <div className="text-muted small">Mobile</div>
                                                        <div className="fw-semibold mt-1">
                                                            +{safeText(item?.from?.country_code) === "—" ? "" : safeText(item?.from?.country_code)} {safeText(item?.from?.mobile)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="text-muted small">Email</div>
                                                        <div className="fw-semibold mt-1" style={{ wordBreak: "break-word" }}>{safeText(item?.from?.email)}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <div className="card border-0 bg-light h-100">
                                                <div className="card-body p-3">
                                                    <h6 className="fw-bold p2p-modal-to-label mb-3" style={{ fontSize: "0.95rem" }}>To User</h6>
                                                    <div className="mb-2">
                                                        <div className="text-muted small">User ID</div>
                                                        <div className="fw-semibold mt-1">{safeText(item?.to?.user_id)}</div>
                                                    </div>
                                                    <div className="mb-2">
                                                        <div className="text-muted small">Username</div>
                                                        <div className="fw-semibold mt-1">{safeText(item?.to?.username)}</div>
                                                    </div>
                                                    <div className="mb-2">
                                                        <div className="text-muted small">Name</div>
                                                        <div className="fw-semibold mt-1">{safeText(item?.to?.name)}</div>
                                                    </div>
                                                    <div className="mb-2">
                                                        <div className="text-muted small">Mobile</div>
                                                        <div className="fw-semibold mt-1">
                                                            +{safeText(item?.to?.country_code) === "—" ? "" : safeText(item?.to?.country_code)} {safeText(item?.to?.mobile)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="text-muted small">Email</div>
                                                        <div className="fw-semibold mt-1" style={{ wordBreak: "break-word" }}>{safeText(item?.to?.email)}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-footer border-top-0 p-3" style={{ backgroundColor: "#f8f9fa" }}>
                                    <button className="btn btn-secondary px-3" type="button" onClick={onClose} style={{ fontSize: "0.85rem" }}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}

export default function P2PReport() {
    const [loading, setLoading] = useState(true);
    const [pageNo, setPageNo] = useState(1);
    const [list, setList] = useState([]);
    const [info, setInfo] = useState({ total: 0, page_no: 1, limit: 20 });
    const [selectedItem, setSelectedItem] = useState(null);

    const totalPages = useMemo(() => {
        const total = Number(info?.total || 0);
        const limit = Number(info?.limit || 20);
        if (!total) return 1;
        return Math.max(1, Math.ceil(total / limit));
    }, [info?.total, info?.limit]);

    const fetchReport = useCallback(async ({ page = 1 } = {}) => {
        setLoading(true);
        try {
            const res = await axios({
                method: "post",
                url: `${BASE_ENDPOINT}/admin/p2p-report`,
                headers: { "Content-Type": "application/json" },
                data: {
                    token: localStorage.getItem("token") || "",
                    user: "",
                    page_no: page,
                    limit: 20,
                },
            });

            const out = res?.data;
            if (out?.error) {
                toast.error(typeof out?.error === "string" ? out.error : "Failed to load P2P report");
                setList([]);
                setInfo({ total: 0, page_no: page, limit: 20 });
                return;
            }

            const rows = Array.isArray(out?.list) ? out.list : [];
            const meta = out?.info || {};

            setList(rows);
            setInfo({
                total: Number(meta?.total || 0),
                page_no: Number(meta?.page_no || page),
                limit: Number(meta?.limit || 20),
            });
        } catch (err) {
            console.log(err);
            toast.error("Failed to load P2P report");
            setList([]);
            setInfo({ total: 0, page_no: page, limit: 20 });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchReport({ page: pageNo });
    }, [pageNo, fetchReport]);

    const onPrev = () => setPageNo((p) => Math.max(1, p - 1));
    const onNext = () => setPageNo((p) => Math.min(totalPages, p + 1));
    const onJump = (p) => setPageNo(Math.min(totalPages, Math.max(1, p)));
    const onRefresh = () => fetchReport({ page: pageNo });

    return (
        <PageTransition>
            <div className="container-fluid py-3 p2p-history-page">
                <div className="p2p-hero mb-4">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div>
                            <h1 className="h3 mb-2 d-flex align-items-center gap-2 fw-bold">
                                <FaPeopleArrows />
                                P2P History
                            </h1>
                            <div className="d-flex flex-wrap align-items-center gap-2">
                                <span className="p2p-stat-pill">
                                    Total transfers: <strong>{nfmt(info.total, 0)}</strong>
                                </span>
                                <span className="p2p-legend p2p-legend-from">From</span>
                                <FaArrowRight className="text-muted" size={12} />
                                <span className="p2p-legend p2p-legend-to">To</span>
                            </div>
                        </div>
                        <button className="btn btn-light p2p-refresh-btn" onClick={onRefresh} disabled={loading}>
                            <FaSyncAlt className={`me-2 ${loading ? "spin" : ""}`} />
                            {loading ? "Refreshing..." : "Refresh"}
                        </button>
                    </div>
                </div>

                <div className="row">
                    <div className="col">
                        <div className="card p2p-table-card border-0 shadow-sm">
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0 p2p-history-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: "8%" }}>S.No</th>
                                                <th style={{ width: "20%" }}>Date</th>
                                                <th style={{ width: "15%" }}>Amount</th>
                                                <th style={{ width: "27%" }}>From</th>
                                                <th style={{ width: "27%" }}>To</th>
                                                <th style={{ width: "6%" }} className="text-center">
                                                    <FaInfoCircle size={12} title="Info" />
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <>
                                                    {[...Array(5)].map((_, i) => (
                                                        <LoadingRow key={i} cols={6} />
                                                    ))}
                                                </>
                                            ) : list.length === 0 ? (
                                                <tr>
                                                    <td colSpan={6} className="text-center py-5">
                                                        <div className="text-muted">
                                                            <FaSearch className="mb-2" size={24} />
                                                            <p className="mb-0">No P2P report found</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                list.map((r, idx) => (
                                                    <tr key={`${r?.transfer_id || ""}_${idx}`}>
                                                        <td className="fw-semibold">
                                                            {(pageNo - 1) * Number(info?.limit || 20) + idx + 1}
                                                        </td>
                                                        <td>{formatDateTime(r?.create_date)}</td>
                                                        <td className="fw-bold p2p-amount">{nfmt(r?.amount, 2)}</td>
                                                        <td>
                                                            <div className="p2p-user-chip p2p-user-from">
                                                                <div className="fw-semibold">{safeText(r?.from?.name)}</div>
                                                                <small>ID: {safeText(r?.from?.user_id)}</small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="p2p-user-chip p2p-user-to">
                                                                <div className="fw-semibold">{safeText(r?.to?.name)}</div>
                                                                <small>ID: {safeText(r?.to?.user_id)}</small>
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-primary px-1 py-0 p2p-info-btn"
                                                                onClick={() => setSelectedItem(r)}
                                                                title="View details"
                                                            >
                                                                <FaInfoCircle size={12} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {!loading && list.length > 0 && (
                                    <div className="card-footer">
                                        <PaginationBar
                                            loading={loading}
                                            pageNo={pageNo}
                                            totalPages={totalPages}
                                            onPrev={onPrev}
                                            onNext={onNext}
                                            onJump={onJump}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <P2PDetailsModal open={!!selectedItem} item={selectedItem} onClose={() => setSelectedItem(null)} />

                <style jsx>{`
                    .p2p-history-page {
                        --p2p-blue: #0d6efd;
                        --p2p-indigo: #6610f2;
                    }
                    .p2p-hero {
                        border-radius: 16px;
                        padding: 1.25rem 1.5rem;
                        background: linear-gradient(135deg, rgba(13, 110, 253, 0.14), rgba(102, 16, 242, 0.12));
                        border: 1px solid rgba(13, 110, 253, 0.2);
                    }
                    .p2p-stat-pill {
                        display: inline-flex;
                        align-items: center;
                        gap: 0.35rem;
                        padding: 0.35rem 0.75rem;
                        border-radius: 999px;
                        background: rgba(255, 255, 255, 0.88);
                        font-size: 0.875rem;
                    }
                    .p2p-legend {
                        display: inline-flex;
                        padding: 0.3rem 0.65rem;
                        border-radius: 999px;
                        font-size: 0.75rem;
                        font-weight: 700;
                    }
                    .p2p-legend-from {
                        color: #b02a37;
                        background: rgba(220, 53, 69, 0.12);
                        border: 1px solid rgba(220, 53, 69, 0.28);
                    }
                    .p2p-legend-to {
                        color: #146c43;
                        background: rgba(25, 135, 84, 0.14);
                        border: 1px solid rgba(25, 135, 84, 0.28);
                    }
                    .p2p-refresh-btn {
                        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
                    }
                    .p2p-table-card {
                        border-radius: 14px;
                        overflow: hidden;
                    }
                    .p2p-history-table thead {
                        background: linear-gradient(90deg, #0d6efd, #6610f2);
                        color: #fff;
                    }
                    .p2p-history-table thead th {
                        font-weight: 600;
                        font-size: 0.75rem;
                        text-transform: uppercase;
                        letter-spacing: 0.04em;
                        border: none;
                        padding: 12px;
                    }
                    .p2p-history-table tbody tr:hover {
                        background: rgba(13, 110, 253, 0.04);
                    }
                    .p2p-history-table td {
                        vertical-align: middle;
                        font-size: 0.9rem;
                    }
                    .p2p-amount {
                        color: #6610f2;
                        font-size: 0.95rem;
                    }
                    .p2p-user-chip {
                        display: inline-block;
                        padding: 0.45rem 0.65rem;
                        border-radius: 10px;
                        min-width: 120px;
                    }
                    .p2p-user-chip small {
                        opacity: 0.85;
                        font-size: 0.75rem;
                    }
                    .p2p-user-chip.p2p-user-from {
                        color: #b02a37;
                        background: rgba(220, 53, 69, 0.08);
                        border-left: 3px solid #dc3545;
                    }
                    .p2p-user-chip.p2p-user-to {
                        color: #146c43;
                        background: rgba(25, 135, 84, 0.08);
                        border-left: 3px solid #198754;
                    }
                    .p2p-modal-header {
                        background: linear-gradient(135deg, #0d6efd, #6610f2);
                    }
                    .p2p-modal-from-label {
                        color: #dc3545;
                    }
                    .p2p-modal-to-label {
                        color: #198754;
                    }
                    .p2p-info-btn:hover {
                        background: #6610f2;
                        border-color: #6610f2;
                        color: #fff;
                    }
                    .spin {
                        animation: spin 1s linear infinite;
                    }
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        </PageTransition>
    );
}

